import { useEffect, useRef } from 'react';
import { loadGoogleSDK } from '../../../dashboard/js/googleSDK';
import { GOOGLE_CLIENT_ID } from './constants';

const MAX_INIT_ATTEMPTS = 60;
const INIT_POLL_INTERVAL_MS = 150;

export default function useGoogleSignIn(onSuccess, onUnavailable) {
    const clientRef = useRef(null);
    const onSuccessRef = useRef(onSuccess);
    const inFlightRef = useRef(false);

    useEffect(() => {
        onSuccessRef.current = onSuccess;
    });

    // The slim shell may inject the Google SDK before this mounts, so loadGoogleSDK's
    // onLoad won't fire; poll for window.google.accounts.oauth2 instead.
    useEffect(() => {
        let cancelled = false;
        let timer = null;
        let attempts = 0;

        const init = () => {
            if (cancelled) return;
            if (window.google && window.google.accounts && window.google.accounts.oauth2) {
                clientRef.current = window.google.accounts.oauth2.initTokenClient({
                    client_id: GOOGLE_CLIENT_ID,
                    scope: 'email profile',
                    callback: tokenResponse => {
                        inFlightRef.current = false;
                        if (tokenResponse && tokenResponse.access_token) {
                            onSuccessRef.current(tokenResponse.access_token);
                        }
                    },
                    // GIS routes a dismissed popup here, not to callback; clear the guard so a re-click works.
                    error_callback: () => {
                        inFlightRef.current = false;
                    },
                });
                return;
            }
            attempts += 1;
            if (attempts >= MAX_INIT_ATTEMPTS) {
                return;
            }
            loadGoogleSDK();
            timer = setTimeout(init, INIT_POLL_INTERVAL_MS);
        };
        init();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, []);

    const signIn = trackingParams => {
        if (inFlightRef.current) return;
        if (!clientRef.current) {
            if (onUnavailable) onUnavailable();
            return;
        }
        window.mixpanel.track('google_sign_up_btn_click', trackingParams);
        inFlightRef.current = true;
        clientRef.current.requestAccessToken();
    };

    return { signIn };
}
